import { createContext, useContext, useState, useRef, useEffect } from "react";
import { X, Send, Loader2, Copy, Check, Phone, ExternalLink, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { ConciergeIcon } from "@/components/ConciergeIcon";

const Ctx = createContext(null);
export const useConcierge = () => useContext(Ctx);

const I18N = {
  it: {
    ask: "Concierge AI", title: "Concierge AI", sub: "Il tuo assistente digitale, disponibile 24/7.",
    empty: "Ciao! 👋 Sono il Concierge AI di StayGuide.", emptySub: "Come posso aiutarti durante il tuo soggiorno?",
    placeholder: "Scrivi un messaggio…", error: "Ops, qualcosa è andato storto. Riprova tra poco.",
    tooMany: "Hai inviato troppi messaggi. Riprova tra poco.", typing: "Concierge AI sta scrivendo…",
    cardTitle: "Hai bisogno di qualcosa?", cardSub: "Chiedi al Concierge AI della struttura. Può aiutarti con Wi-Fi, check-in, parcheggio, casa e consigli locali.",
    suggestions: ["Come faccio il check-in?", "Qual è la password Wi-Fi?", "Dove posso parcheggiare?", "Come funziona l'aria condizionata?", "Dove posso mangiare?", "A che ora è il check-out?"],
  },
  en: {
    ask: "Concierge AI", title: "Concierge AI", sub: "Your digital assistant, available 24/7.",
    empty: "Hi! 👋 I'm StayGuide's Concierge AI.", emptySub: "How can I help during your stay?",
    placeholder: "Type a message…", error: "Oops, something went wrong. Please try again shortly.",
    tooMany: "You've sent too many messages. Please try again shortly.", typing: "Concierge AI is typing…",
    cardTitle: "Need something?", cardSub: "Ask the property's Concierge AI. It can help with Wi-Fi, check-in, parking, the home and local tips.",
    suggestions: ["How do I check in?", "What's the Wi-Fi password?", "Where can I park?", "How does the air conditioning work?", "Where can I eat?", "What time is check-out?"],
  },
  fr: {
    ask: "Concierge AI", title: "Concierge AI", sub: "Votre assistant digital, disponible 24h/24.",
    empty: "Bonjour ! 👋 Je suis le Concierge AI de StayGuide.", emptySub: "Comment puis-je vous aider pendant votre séjour ?",
    placeholder: "Écrivez un message…", error: "Oups, une erreur s'est produite. Réessayez dans un instant.",
    tooMany: "Vous avez envoyé trop de messages. Réessayez dans un instant.", typing: "Concierge AI est en train d'écrire…",
    cardTitle: "Besoin de quelque chose ?", cardSub: "Demandez au Concierge AI du logement. Il peut vous aider pour le Wi-Fi, le check-in, le parking, le logement et les bonnes adresses.",
    suggestions: ["Comment faire le check-in ?", "Quel est le mot de passe Wi-Fi ?", "Où puis-je me garer ?", "Comment fonctionne la climatisation ?", "Où puis-je manger ?", "À quelle heure est le check-out ?"],
  },
  de: {
    ask: "Concierge AI", title: "Concierge AI", sub: "Dein digitaler Assistent, rund um die Uhr verfügbar.",
    empty: "Hallo! 👋 Ich bin der Concierge AI von StayGuide.", emptySub: "Wie kann ich dir während deines Aufenthalts helfen?",
    placeholder: "Nachricht schreiben…", error: "Ups, etwas ist schiefgelaufen. Bitte versuche es gleich noch einmal.",
    tooMany: "Du hast zu viele Nachrichten gesendet. Bitte versuche es gleich noch einmal.", typing: "Concierge AI schreibt…",
    cardTitle: "Brauchst du etwas?", cardSub: "Frag den Concierge AI der Unterkunft. Er hilft bei WLAN, Check-in, Parken, der Wohnung und lokalen Tipps.",
    suggestions: ["Wie funktioniert der Check-in?", "Wie lautet das WLAN-Passwort?", "Wo kann ich parken?", "Wie funktioniert die Klimaanlage?", "Wo kann ich essen?", "Wann ist der Check-out?"],
  },
};

export function ConciergeProvider({ enabled, mode = "public", cid, brand = "#2E7CB8", T, lang = "it", children }) {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState(null);
  const [cardVisible, setCardVisible] = useState(false);
  const openChat = (q) => { setSeed(q || null); setOpen(true); };
  const t = I18N[lang] || I18N.it;
  if (!enabled) return <>{children}</>;
  return (
    <Ctx.Provider value={{ open: openChat, setCardVisible, lang }}>
      {children}
      <div className="fixed inset-0 z-[60] pointer-events-none flex justify-center">
        <div className="relative w-full max-w-md h-full">
          {!open && !cardVisible && (
            <button onClick={() => openChat()} data-testid="concierge-fab"
              className="pointer-events-auto absolute bottom-24 right-4 inline-flex items-center gap-2 rounded-full pl-4 pr-5 py-3 text-white font-semibold text-sm shadow-xl active:scale-95 transition-transform"
              style={{ background: brand, boxShadow: `0 10px 30px ${brand}66` }}>
              <span className="w-6 h-6 rounded-full bg-white grid place-items-center"><ConciergeIcon className="w-4 h-4" animated /></span> {t.ask}
            </button>
          )}
          {open && <ChatSheet mode={mode} cid={cid} brand={brand} T={T} lang={lang} seed={seed} onClose={() => setOpen(false)} />}
        </div>
      </div>
    </Ctx.Provider>
  );
}

function ChatSheet({ mode, cid, brand, T, seed, onClose, lang = "it" }) {
  const t = I18N[lang] || I18N.it;
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState("");
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const dark = T?.dark;

  useEffect(() => {
    try { sessionRef.current = sessionStorage.getItem(`sg_concierge_${cid}`) || null; } catch (e) { /* noop */ }
  }, [cid]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const url = mode === "preview" ? `/properties/${cid}/concierge/preview` : `/public/guide/${cid}/concierge`;
      let guestToken = null;
      try { guestToken = sessionStorage.getItem(`sg_guest_${cid}`); } catch (e) { /* noop */ }
      const { data } = await api.post(url, { message: q, session_id: sessionRef.current, lang, guest_token: guestToken });
      sessionRef.current = data.session_id;
      try { sessionStorage.setItem(`sg_concierge_${cid}`, data.session_id); } catch (e) { /* noop */ }
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, actions: data.actions || [] }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: e.response?.status === 429 ? t.tooMany : t.error, actions: [] }]);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (seed) send(seed); /* eslint-disable-next-line */ }, [seed]);

  const copy = (v, k) => { navigator.clipboard?.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };

  const Action = ({ a, i }) => {
    const base = "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold active:scale-95 transition-transform";
    const style = { background: `${brand}18`, color: brand };
    if (a.type === "copy")
      return <button data-testid={`concierge-action-${i}`} onClick={() => copy(a.value, `a${i}`)} className={base} style={style}>{copied === `a${i}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{a.label}</button>;
    if (a.type === "tel")
      return <a data-testid={`concierge-action-${i}`} href={`tel:${a.value}`} className={base} style={style}><Phone className="w-3.5 h-3.5" />{a.label}</a>;
    return <a data-testid={`concierge-action-${i}`} href={a.value} target="_blank" rel="noreferrer" className={base} style={style}><ExternalLink className="w-3.5 h-3.5" />{a.label}</a>;
  };

  return (
    <div className="pointer-events-auto absolute inset-0 z-[61] flex flex-col" data-testid="concierge-sheet"
      style={{ background: dark ? "#0e0e12" : "#f8fafc" }}>
      <div className="flex items-center gap-3 px-4 py-3.5 text-white shrink-0" style={{ background: brand }}>
        <div className="w-9 h-9 rounded-full bg-white grid place-items-center shrink-0"><ConciergeIcon className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm leading-tight">{t.title}</p>
          <p className="text-[11px] text-white/80">{t.sub}</p>
        </div>
        <button onClick={onClose} data-testid="concierge-close" className="w-8 h-8 grid place-items-center rounded-full bg-white/15 active:scale-90 transition-transform"><X className="w-4 h-4" /></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {msgs.length === 0 && !loading && (
          <div className="text-center pt-4">
            <div className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-3 bg-white shadow-sm border" style={{ borderColor: dark ? "#26262e" : "#eef2f7" }}><ConciergeIcon className="w-10 h-10" animated /></div>
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: brand }}>Concierge AI</p>
            <p className="font-serif-display text-lg mt-1" style={{ color: dark ? "#fff" : "#0f172a" }}>{t.empty}</p>
            <p className="text-sm mt-1" style={{ color: dark ? "#9ca3af" : "#64748b" }}>{t.emptySub}</p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {t.suggestions.map((s, i) => (
                <button key={i} data-testid={`concierge-suggestion-${i}`} onClick={() => send(s)}
                  className="rounded-full border px-3.5 py-2 text-xs font-medium active:scale-95 transition-transform"
                  style={{ borderColor: dark ? "#2a2a33" : "#e2e8f0", color: dark ? "#e5e7eb" : "#334155", background: dark ? "#17171d" : "#fff" }}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className="max-w-[82%]">
              <div data-testid={`concierge-message-${m.role}`} className="rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap"
                style={m.role === "user"
                  ? { background: brand, color: "#fff", borderBottomRightRadius: 6 }
                  : { background: dark ? "#17171d" : "#fff", color: dark ? "#e5e7eb" : "#1f2a44", border: `1px solid ${dark ? "#26262e" : "#eef2f7"}`, borderBottomLeftRadius: 6 }}>
                {m.content}
              </div>
              {m.role === "assistant" && m.actions?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">{m.actions.map((a, k) => <Action key={k} a={a} i={`${i}-${k}`} />)}</div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start items-center gap-2" data-testid="concierge-typing">
            <span className="w-8 h-8 rounded-full bg-white grid place-items-center border shrink-0" style={{ borderColor: dark ? "#26262e" : "#eef2f7" }}><ConciergeIcon className="w-5 h-5" animated /></span>
            <span className="text-[13px]" style={{ color: dark ? "#9ca3af" : "#64748b" }}>{t.typing}</span>
          </div>
        )}
      </div>

      <div className="shrink-0 p-3 border-t flex items-center gap-2" style={{ borderColor: dark ? "#26262e" : "#e2e8f0", background: dark ? "#0e0e12" : "#f8fafc", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="concierge-input" placeholder={t.placeholder}
          className="flex-1 rounded-full px-4 py-3 text-sm outline-none"
          style={{ background: dark ? "#17171d" : "#fff", color: dark ? "#fff" : "#0f172a", border: `1px solid ${dark ? "#26262e" : "#e2e8f0"}` }} />
        <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="concierge-send"
          className="w-11 h-11 rounded-full grid place-items-center text-white shrink-0 disabled:opacity-50 active:scale-90 transition-transform" style={{ background: brand }}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export function ConciergeHomeCard({ brand = "#2E7CB8", T }) {
  const ctx = useConcierge();
  const ref = useRef(null);
  const setCardVisible = ctx?.setCardVisible;
  const lang = ctx?.lang || "it";
  const t = I18N[lang] || I18N.it;
  useEffect(() => {
    const el = ref.current;
    if (!el || !setCardVisible) return;
    const io = new IntersectionObserver(([e]) => setCardVisible(e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => { setCardVisible(false); io.disconnect(); };
  }, [setCardVisible]);
  if (!ctx) return null;
  const surface = { background: T?.surface, borderColor: T?.border };
  return (
    <div ref={ref} className="px-5 pt-6" data-testid="concierge-home-card">
      <div className="rounded-3xl border p-5 relative overflow-hidden" style={{ ...surface, boxShadow: T?.dark ? "none" : "0 1px 3px rgba(15,23,42,0.06)" }}>
        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-20" style={{ background: brand }} />
        <div className="flex items-center gap-2.5 relative">
          <div className="w-11 h-11 rounded-2xl grid place-items-center bg-white shadow-sm border border-slate-100"><ConciergeIcon className="w-7 h-7" animated /></div>
          <p className="font-serif-display text-lg" style={{ color: T?.text }}>{t.cardTitle}</p>
        </div>
        <p className="text-sm mt-2 leading-relaxed relative" style={{ color: T?.dark ? T?.muted : "#4B5563" }}>
          {t.cardSub}
        </p>
        <div className="mt-3 flex flex-wrap gap-2 relative">
          {t.suggestions.slice(0, 3).map((s, i) => (
            <button key={i} data-testid={`concierge-card-suggestion-${i}`} onClick={() => ctx.open(s)}
              className="rounded-full border px-3 py-1.5 text-xs font-medium active:scale-95 transition-transform"
              style={{ borderColor: T?.border, color: T?.text }}>{s}</button>
          ))}
        </div>
        <button onClick={() => ctx.open()} data-testid="concierge-home-cta"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-white font-semibold text-sm active:scale-95 transition-transform relative" style={{ background: brand }}>
          <span className="w-5 h-5 rounded-full bg-white grid place-items-center"><ConciergeIcon className="w-3.5 h-3.5" /></span> {t.ask} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
